import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Avatar } from "../components/ui/Avatar";
import { EmptyState } from "../components/ui/EmptyState";
import { signOut } from "../features/auth/api";
import { useSession } from "../features/auth/hooks";
import { useMe } from "../features/auth/useMe";

export function ProfileRoute() {
  const { t } = useTranslation();
  const { user } = useSession();
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const [signOutError, setSignOutError] = useState(false);

  async function handleSignOut() {
    setSignOutError(false);
    try {
      await signOut();
    } catch (err) {
      console.error("[ProfileRoute] signOut failed", err);
      setSignOutError(true);
    }
  }

  return (
    <div className="space-y-4 pt-3">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-fg">
          {t("profile.identity_title")}
        </h1>
      </header>

      {user ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3 rounded-lg border border-line bg-surface p-4">
            <Avatar userId={user.id} label={user.email} size={48} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-fg">{user.email ?? user.id}</p>
              <button
                type="button"
                onClick={handleSignOut}
                className="text-xs text-fg-mute hover:text-fg"
              >
                {t("profile.signout")}
              </button>
            </div>
          </div>
          {signOutError && (
            <EmptyState
              tone="ember"
              glyph="⚠"
              title={t("profile.signout_failed")}
              action={
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="btn-ghost inline-flex items-center px-4 py-2 text-sm"
                >
                  {t("common.retry")}
                </button>
              }
            />
          )}
        </div>
      ) : (
        <EmptyState
          glyph="♡"
          eyebrow={t("profile.unauth_eyebrow")}
          title={t("profile.unauth_title")}
          body={t("profile.unauth_body")}
          action={
            <Link to="/auth/login" className="btn-volt inline-flex items-center px-4 py-2 text-sm">
              {t("profile.unauth_cta")}
            </Link>
          }
        />
      )}

      <nav className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {(isAdmin
          ? [...SUB_ROUTES, { to: "/admin/verify", i18nKey: "profile.row_admin_verify" }]
          : SUB_ROUTES
        ).map((row) => (
          <Link
            key={row.to}
            to={row.to}
            className="flex items-center justify-between rounded-lg border border-line bg-surface p-4 text-fg hover:border-line-strong"
          >
            <span>{t(row.i18nKey)}</span>
            <span aria-hidden className="text-fg-mute">
              ›
            </span>
          </Link>
        ))}
      </nav>
    </div>
  );
}

const SUB_ROUTES: { to: string; i18nKey: string }[] = [
  { to: "/favorites", i18nKey: "profile.row_favorites" },
  { to: "/stations/request", i18nKey: "stations.request.profile_row" },
  { to: "/how-to", i18nKey: "profile.row_howto" },
  { to: "/home", i18nKey: "profile.row_about" },
  { to: "/settings", i18nKey: "profile.row_settings" },
  { to: "/privacy", i18nKey: "profile.row_privacy" },
];
